<template>
  <div
    ref="scrollEl"
    class="h-full overflow-y-auto overflow-x-hidden relative"
    @scroll="onScroll"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
    @touchcancel="onTouchEnd"
  >
    <div
      class="absolute top-0 left-0 right-0 flex items-center justify-center overflow-hidden pointer-events-none"
      :style="{ height: pullDistance + 'px' }"
    >
      <i
        class="fa-solid fa-arrows-rotate text-gray-500"
        :class="{ 'animate-spin': refreshing }"
        :style="{ transform: refreshing ? '' : `rotate(${pullDistance * 3}deg)`, opacity: Math.min(pullDistance / threshold, 1) }"
      ></i>
    </div>
    <div :style="contentStyle">
      <slot />
    </div>
  </div>
</template>

<script>
const DEAD_ZONE = 15;
const MIN_DWELL_AT_TOP_MS = 400;
const MIN_GESTURE_MS = 300;
const IGNORE_SELECTOR = 'input, select, textarea, button, a, [role="button"]';

export default {
  name: 'PullToRefresh',
  data() {
    return {
      startY: null,
      startX: null,
      startedAt: 0,
      locked: false,
      aborted: false,
      pullDistance: 0,
      refreshing: false,
      // When the container is scrolled to top, this holds the timestamp it
      // arrived there. A fresh touch only arms the pull gesture once we've
      // been resting at top for a bit, so a swipe that merely *ends up* at
      // scrollTop 0 (e.g. one of several quick swipes scrolling back up)
      // isn't mistaken for a deliberate pull-to-refresh.
      topSince: Date.now(),
      threshold: 70,
      maxPull: 120,
    };
  },
  computed: {
    contentStyle() {
      return {
        // 'none' (not translateY(0)) when at rest: any non-none transform on
        // an ancestor creates a new containing block for descendant
        // position:fixed elements (e.g. modals), which would then anchor to
        // and scroll with this div instead of the real viewport.
        transform: this.pullDistance ? `translateY(${this.pullDistance}px)` : 'none',
        transition: this.startY === null ? 'transform 0.2s ease' : 'none',
      };
    },
  },
  methods: {
    onScroll() {
      if (this.$refs.scrollEl.scrollTop > 0) {
        this.topSince = 0;
      } else if (!this.topSince) {
        this.topSince = Date.now();
      }
    },
    onTouchStart(e) {
      this.locked = false;
      this.aborted = false;
      this.pullDistance = 0;
      const atRestAtTop = this.$refs.scrollEl.scrollTop <= 0
        && this.topSince
        && Date.now() - this.topSince >= MIN_DWELL_AT_TOP_MS;
      if (this.refreshing || !atRestAtTop || e.target.closest(IGNORE_SELECTOR)) {
        this.startY = null;
        return;
      }
      this.startX = e.touches[0].clientX;
      this.startY = e.touches[0].clientY;
      this.startedAt = Date.now();
    },
    onTouchMove(e) {
      if (this.startY === null || this.refreshing || this.aborted) return;
      const touch = e.touches[0];
      const deltaY = touch.clientY - this.startY;
      const deltaX = touch.clientX - this.startX;

      if (!this.locked) {
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > DEAD_ZONE) {
          this.aborted = true;
          return;
        }
        if (Math.abs(deltaY) < DEAD_ZONE) return;
        this.locked = true;
      }

      if (deltaY > 0 && this.$refs.scrollEl.scrollTop <= 0) {
        this.pullDistance = Math.min((deltaY - DEAD_ZONE) * 0.4, this.maxPull);
        e.preventDefault();
      } else {
        this.pullDistance = 0;
        this.aborted = true;
      }
    },
    onTouchEnd() {
      if (this.startY === null) return;
      this.startY = null;
      // A fast flick can cover a lot of distance in very little time (e.g. a
      // normal scroll fling that happens to end at the top) - require the
      // pull to also have been held for a bit, which a deliberate slow drag
      // naturally has and a quick flick doesn't.
      const heldLongEnough = Date.now() - this.startedAt >= MIN_GESTURE_MS;
      if (this.pullDistance >= this.threshold && heldLongEnough) {
        this.refreshing = true;
        this.pullDistance = this.threshold;
        window.location.reload();
      } else {
        this.pullDistance = 0;
      }
    },
  },
};
</script>
